/**
 * Utilidades comunes para componentes de mapas
 * Funciones reutilizables para estilos, bounds, máscaras y configuraciones
 */

// Eliminado import de Leaflet

/**
 * Estilos para features de mapas con diferentes niveles de riesgo
 * @param {Object} feature - Feature del GeoJSON
 * @param {number} layerOrder - Orden de la capa
 * @returns {Object} - Objeto de estilo para Leaflet
 */
export const getFeatureStyle = (feature, layerOrder = 0) => {
  const risk = feature.properties.CONVECTIVE_MATRIX_RISK;
  const opacityLevels = [0.65, 0.65, 0.65, 0.65, 0.65];
  const opacity = opacityLevels[layerOrder % opacityLevels.length];

  const baseStyle = {
    color: '#000000',
    opacity: opacity,
    weight: 0,
  };

  switch (risk) {
    case 5:
      return {
        ...baseStyle,
        fillColor: '#6A5ACD',
        fillOpacity: opacity,
        zIndex: 50000,
      };
    case 4:
      return {
        ...baseStyle,
        fillColor: '#DC143C',
        fillOpacity: opacity,
        zIndex: 40000,
      };
    case 3:
      return {
        ...baseStyle,
        fillColor: '#FF8C00',
        fillOpacity: opacity,
        zIndex: 30000,
      };
    case 2:
      return {
        ...baseStyle,
        fillColor: '#ffff00',
        fillOpacity: opacity,
        zIndex: 20000,
      };
    case 1:
      return {
        ...baseStyle,
        fillColor: '#2E8B57',
        fillOpacity: opacity,
        zIndex: 10000,
      };
    default:
      return {
        ...baseStyle,
        fillColor: '#FFFFFF',
        fillOpacity: 0,
        zIndex: 1,
      };
  }
};

/**
 * Estilos específicos para features CNN
 * @param {Object} feature - Feature del GeoJSON
 * @param {number} layerOrder - Orden de la capa
 * @returns {Object} - Objeto de estilo para Leaflet
 */
export const getCNNFeatureStyle = (feature, layerOrder = 0) => {
  const risk = feature.properties.CONVECTIVE_MATRIX_RISK;
  const opacityLevels = [0.65, 0.65, 0.65, 0.65, 0.65];
  const opacity = opacityLevels[layerOrder % opacityLevels.length];

  const baseStyle = {
    color: '#000000',
    opacity: opacity,
    weight: 0,
  };

  switch (risk) {
    case 4:
      return {
        ...baseStyle,
        fillColor: '#6A5ACD',
        fillOpacity: 0,
        zIndex: 50000,
      };
    case 3:
      return {
        ...baseStyle,
        fillColor: '#DC143C',
        fillOpacity: opacity,
        zIndex: 40000,
      };
    case 2:
      return {
        ...baseStyle,
        fillColor: '#FF8C00',
        fillOpacity: opacity,
        zIndex: 30000,
      };
    case 1:
      return {
        ...baseStyle,
        fillColor: '#ffff00',
        fillOpacity: opacity,
        zIndex: 20000,
      };
    default:
      return {
        ...baseStyle,
        fillColor: '#FFFFFF',
        fillOpacity: 0,
        zIndex: 1,
      };
  }
};

/**
 * Calcula los bounds del rectángulo basado en el tipo de capa
 * @param {string} layerType - Tipo de capa ('01' o '025')
 * @returns {Array} - Array de coordenadas [[lat, lon], [lat, lon]]
 */
export const calculateMapBounds = (layerType) => {
  if (!layerType) return null;
  
  if (layerType.includes('00625')) {
    return [[33, -10.0625], [56.4375, 21.6875]];
  }
  
  return layerType.includes('01')
    ? [[33, -16], [64, 45]]  // bounds para 01
    : [[33, -27], [73, 45]]; // bounds para 025
};

/**
 * Genera el polígono de máscara exterior
 * @param {Array} rectBounds - Bounds del rectángulo
 * @returns {Array|null} - Array de coordenadas para el polígono de máscara
 */
export const createMaskPolygon = (rectBounds) => {
  if (!rectBounds) return null;
  
  const [[latN, lonW], [latS, lonE]] = rectBounds;
  const innerRing = [
    [latN, lonW],
    [latN, lonE],
    [latS, lonE],
    [latS, lonW]
  ];
  
  const outerRing = [
    [90, -180],
    [90, 180],
    [-90, 180],
    [-90, -180]
  ];
  
  return [outerRing, innerRing];
};

/**
 * Configuración común para MapContainer
 */
export const MAP_CONFIG = {
  minZoom: 2.4,             // Aumentado para evitar ver demasiados mapas (zoom out restringido)
  maxZoom: 16,
  height: "100%",
  width: "100%",
  zoomSnap: 0,              // Zoom continuo (fraccional) para máxima suavidad
  wheelPxPerZoomLevel: 950,
  zoomDelta: 1.0,
  zoomAnimation: true,
  zoomAnimationThreshold: 10,
  inertia: true,            // Habilita inercia para un movimiento más natural
  wheelDebounceTime:50,    // Tiempo de espera entre eventos de rueda
};

/**
 * Configuración común para throttle y debounce
 */
export const THROTTLE_CONFIG = {
  FETCH_INTERVAL: 250,      // Aumentado significativamente para evitar lag durante zoom/pan rápido
  DEBOUNCE_INTERVAL: 100,   // Mayor espera para asegurar que el usuario ha terminado de moverse
};

// createCanvasRenderer eliminado (específico de Leaflet)

/**
 * Configuración de paths para rectángulos de bounds
 */
export const BOUNDS_RECTANGLE_STYLE = {
  color: '#141414',
  weight: 1,
  dashArray: '3,5',
  fillOpacity: 0
};

/**
 * Configuración de paths para polígonos de máscara
 */
export const MASK_POLYGON_STYLE = {
  fillColor: 'gray',
  fillOpacity: 0.18,
  stroke: false
};

/**
 * URL base para tiles
 */
export const TILE_LAYER_URL = "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}";

/**
 * Genera nombre de archivo para descarga de GeoJSON
 * @param {string} mapLayer - Capa del mapa
 * @param {string} cqlFecha - Fecha CQL
 * @returns {string} - Nombre del archivo
 */
export const generateGeoJSONFileName = (mapLayer, cqlFecha) => {
  const layerType = mapLayer.includes('025') ? 'EU_025' : 'EU_0125';
  return `geojson_${layerType}_${cqlFecha}.geojson`;
};

/**
 * Ordena features por riesgo convectivo
 * @param {Array} features - Array de features
 * @returns {Array} - Features ordenados
 */
export const sortFeaturesByRisk = (features) => {
  return features.sort((b, a) => b.properties.CONVECTIVE_MATRIX_RISK - a.properties.CONVECTIVE_MATRIX_RISK);
};
